import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Search, MessageSquare, Eye, Plus, Minus, X, ShoppingCart, CreditCard, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import useOrderDocumentWorkflow from '@/hooks/useOrderDocumentWorkflow';
import useOrdersPolling from '@/hooks/useOrdersPolling';
import OrderDocumentsPanel from '@/components/orders/OrderDocumentsPanel';
import OrderDocumentPreviewDialog from '@/components/orders/OrderDocumentPreviewDialog';

const API = API_BASE_URL;

const statusColors = {
  new: 'bg-blue-100 text-blue-800',
  preparing: 'bg-yellow-100 text-yellow-800',
  ready: 'bg-green-100 text-green-800',
  completed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
};

const paymentStatusColors = {
  unpaid: 'bg-amber-100 text-amber-900',
  paid: 'bg-emerald-100 text-emerald-900',
  refunded: 'bg-rose-100 text-rose-900',
  legacy_unknown: 'bg-gray-100 text-gray-700',
};

const Orders = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const scopeKey = JSON.stringify([user?.id, user?.role, user?.company_id, user?.restaurant_id]);
  const { orders, initialLoading, initialError, refreshError, refresh: fetchOrders, refreshAfterMutation } =
    useOrdersPolling({ apiBaseUrl: API, status: statusFilter, scopeKey });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [currency, setCurrency] = useState('MYR');
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashReceived, setCashReceived] = useState('');
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [refundOrder, setRefundOrder] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [newOrder, setNewOrder] = useState({
    customer_id: '',
    items: [],
    payment_method: 'cash',
    delivery_address: '',
    notes: ''
  });

  const {
    previewOpen,
    setPreviewOpen,
    previewLoading,
    previewDocument,
    pdfSaving,
    printLoading,
    previewDocumentType,
    activityLoading,
    documentActivities,
    thermalPreviewRef,
    openDocumentPreview,
    queueDocumentAction,
    refreshDocumentPreview,
    saveAsPdf,
    browserPrint,
    handleReprint,
    hasDocumentActivity,
    closePreview,
  } = useOrderDocumentWorkflow({
    apiBaseUrl: API,
    selectedOrderId: selectedOrder?.id,
  });

  const fetchCustomers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/customers`, { withCredentials: true });
      setCustomers(data);
    } catch (error) { console.error(error); }
  }, []);

  const fetchMenu = useCallback(async () => {
    try {
      const { data } = await axios.get(
  `${API}/menu?restaurant_id=${localStorage.getItem("restaurant_id")}`,
  { withCredentials: true }
);
      setMenuItems(data.filter(m => m.available));
      try {
  const restaurantRes = await axios.get(
    `${API}/restaurants`,
    { withCredentials: true }
  );

  if (restaurantRes.data.length > 0) {
    setCurrency(restaurantRes.data[0].currency || 'USD');
  }
} catch (err) {
  console.error(err);
}
    } catch (error) { console.error(error); }
  }, []);

  useEffect(() => {
    fetchCustomers();
    fetchMenu();
  }, [fetchCustomers, fetchMenu]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/orders/${orderId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success('Order updated');
      refreshAfterMutation();
    } catch (error) { toast.error('Failed'); }
  };

  const updateOrderPaymentState = (orderId, payment) => {
    refreshAfterMutation((current) => current.map((order) => order.id === orderId ? { ...order, ...payment } : order));
    setSelectedOrder((current) => current?.id === orderId ? { ...current, ...payment } : current);
  };

  const openPaymentDialog = (order) => {
    setPaymentOrder(order);
    setPaymentMethod(order.payment_method || 'cash');
    setCashReceived('');
  };

  const confirmPayment = async () => {
    if (!paymentOrder || paymentSubmitting) return;
    if (paymentMethod === 'cash' && (!cashReceived || Number(cashReceived) < paymentOrder.total_amount)) {
      toast.error('Cash received must cover the Grand Total');
      return;
    }
    setPaymentSubmitting(true);
    try {
      const payload = { payment_method: paymentMethod };
      if (paymentMethod === 'cash') payload.cash_received = cashReceived;
      const { data } = await axios.post(
        `${API}/orders/${paymentOrder.id}/payments/confirm`,
        payload,
        { withCredentials: true }
      );
      updateOrderPaymentState(paymentOrder.id, data);
      setPaymentOrder(null);
      toast.success('Payment confirmed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm payment');
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const confirmRefund = async () => {
    if (!refundOrder || refundSubmitting || !refundReason.trim()) return;
    setRefundSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/orders/${refundOrder.id}/refunds`,
        { reason: refundReason.trim() },
        { withCredentials: true }
      );
      updateOrderPaymentState(refundOrder.id, data);
      setRefundOrder(null);
      setRefundReason('');
      toast.success('Full refund recorded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refund payment');
    } finally {
      setRefundSubmitting(false);
    }
  };

  const canRefund = ['super_admin', 'company_admin', 'restaurant_manager', 'supervisor'].includes(user?.role);
  const cashChange = paymentOrder && cashReceived !== ''
    ? Math.max(Number(cashReceived) - paymentOrder.total_amount, 0)
    : 0;

  const addItemToOrder = (item) => {
    const existing = newOrder.items.find(i => i.menu_id === item.id);
    if (existing) {
      setNewOrder({
        ...newOrder,
        items: newOrder.items.map(i => i.menu_id === item.id ? { ...i, quantity: i.quantity + 1 } : i)
      });
    } else {
      setNewOrder({
        ...newOrder,
        items: [...newOrder.items, { menu_id: item.id, name: item.name, price: item.price, quantity: 1 }]
      });
    }
  };

  const updateQty = (idx, delta) => {
    const items = [...newOrder.items];
    items[idx].quantity = Math.max(1, items[idx].quantity + delta);
    setNewOrder({ ...newOrder, items });
  };

  const removeItem = (idx) => {
    setNewOrder({ ...newOrder, items: newOrder.items.filter((_, i) => i !== idx) });
  };

  const total = newOrder.items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  const createOrder = async () => {
    if (!newOrder.customer_id) { toast.error('Select a customer'); return; }
    if (newOrder.items.length === 0) { toast.error('Add at least one item'); return; }
    
    try {
      const restaurant_id = customers.find(c => c.id === newOrder.customer_id)?.restaurant_id 
        || menuItems[0]?.restaurant_id 
        || 'default-restaurant';
      
      await axios.post(`${API}/orders`, {
        customer_id: newOrder.customer_id,
        restaurant_id,
        items: newOrder.items.map(i => ({ menu_id: i.menu_id, quantity: i.quantity })),
        payment_method: newOrder.payment_method,
        delivery_address: newOrder.delivery_address,
        notes: newOrder.notes
      }, { withCredentials: true });
      
      toast.success('Order created');
      setOpenCreate(false);
      setNewOrder({ customer_id: '', items: [], payment_method: 'cash', delivery_address: '', notes: '' });
      refreshAfterMutation();
    } catch (error) {
      toast.error('Failed to create order');
    }
  };

  const filteredOrders = orders.filter(o => o.order_number?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#232D4B' }}>
          {t('orders')}
        </h1>
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button style={{ backgroundColor: '#E57200' }} data-testid="create-order-btn">
              <ShoppingCart className="h-4 w-4 me-2" /> New Order
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Create New Order</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Customer</Label>
                  <Select value={newOrder.customer_id} onValueChange={(v) => setNewOrder({ ...newOrder, customer_id: v })}>
                    <SelectTrigger data-testid="order-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>
                      {customers.length === 0 ? (
                        <SelectItem value="none" disabled>Add customers first</SelectItem>
                      ) : (
                        customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} - {c.phone}</SelectItem>)
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Payment Method</Label>
                  <Select value={newOrder.payment_method} onValueChange={(v) => setNewOrder({ ...newOrder, payment_method: v })}>
                    <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="online_transfer_qr">Online Transfer / QR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Menu items grid */}
              <div>
                <Label className="mb-2 block">Add Items from Menu</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-2 bg-muted/30 rounded-lg">
                  {menuItems.length === 0 ? (
                    <p className="col-span-full text-center text-muted-foreground text-sm py-4">No menu items available</p>
                  ) : (
                    menuItems.map(item => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => addItemToOrder(item)}
                        className="p-2 bg-white dark:bg-[#131B2B] rounded-lg border border-border/40 hover:border-[#E57200] hover:bg-[#E57200]/5 text-start transition-colors"
                        data-testid={`add-item-${item.id}`}
                      >
                        <div className="text-sm font-medium truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.name}</div>
                        <div className="text-xs font-bold" style={{ color: '#E57200' }}>MYR{item.price.toFixed(2)}</div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Cart */}
              {newOrder.items.length > 0 && (
                <div>
                  <Label className="mb-2 block">Order Items</Label>
                  <div className="space-y-2 max-h-48 overflow-y-auto border border-border/40 rounded-lg p-2">
                    {newOrder.items.map((item, idx) => (
<div key={idx} className="flex items-center gap-2 p-2 bg-muted/30 rounded">
  <div className="flex-1">
    <div className="text-sm font-medium">{item.name}</div>

    <div className="text-xs text-muted-foreground">
      {currency} {item.price.toFixed(2)} x {item.quantity}
    </div>
  </div>

  <Button
    size="icon"
    variant="ghost"
    onClick={() => updateQty(idx, -1)}
    data-testid={`qty-minus-${idx}`}
  >
    <Minus className="h-3 w-3" />
  </Button>

  <span className="w-6 text-center font-medium">
    {item.quantity}
  </span>

  <Button
    size="icon"
    variant="ghost"
    onClick={() => updateQty(idx, 1)}
    data-testid={`qty-plus-${idx}`}
  >
    <Plus className="h-3 w-3" />
  </Button>

  <Button
    size="icon"
    variant="ghost"
    onClick={() => removeItem(idx)}
    data-testid={`remove-item-${idx}`}
  >
    <X className="h-4 w-4" />
  </Button>
</div>                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label>Delivery Address (optional)</Label>
                <Input value={newOrder.delivery_address} onChange={(e) => setNewOrder({ ...newOrder, delivery_address: e.target.value })} data-testid="delivery-address" />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={newOrder.notes} onChange={(e) => setNewOrder({ ...newOrder, notes: e.target.value })} data-testid="order-notes" />
              </div>

<div className="flex items-center justify-between p-3 bg-[#E57200]/10 rounded-lg">
  <span
    className="font-bold"
    style={{ fontFamily: 'Outfit, sans-serif' }}
  >
    Subtotal
  </span>

  <span
    className="text-2xl font-bold"
    style={{ color: '#E57200', fontFamily: 'Outfit, sans-serif' }}
  >
    {currency} {total.toFixed(2)}
  </span>
</div>  
              </div> 
              <DialogFooter>
              <Button onClick={createOrder} style={{ backgroundColor: '#E57200' }} data-testid="save-order-btn">Create Order</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {initialError && <div role="alert">Unable to load orders. <Button onClick={fetchOrders}>Retry</Button></div>}
      {refreshError && <div role="status">Updates delayed - retrying. <Button onClick={fetchOrders}>Retry</Button></div>}
      {initialLoading && <p role="status">Loading orders...</p>}

      <Card className="border-border/40">
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder={t('searchOrders')} value={search} onChange={(e) => setSearch(e.target.value)} className="ps-10" data-testid="search-input" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="preparing">Preparing</SelectItem>
                <SelectItem value="ready">Ready</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table data-testid="orders-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('orderNumber')}</TableHead>
                <TableHead>{t('total')}</TableHead>
                <TableHead>{t('status')}</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">{initialLoading ? 'Loading orders...' : initialError ? 'Unable to load orders' : 'No orders found'}</TableCell></TableRow>
              ) : (
                filteredOrders.map(order => (
                  <TableRow key={order.id} className="hover:bg-muted/50" data-testid={`order-row-${order.id}`}>
                    <TableCell className="font-medium">{order.order_number}</TableCell>
                    <TableCell>RM {order.total_amount.toFixed(2)}</TableCell>
                    <TableCell><Badge className={statusColors[order.status]}>{order.status}</Badge></TableCell>
                    <TableCell>
                      <Badge className={paymentStatusColors[order.payment_status || 'legacy_unknown']}>
                        {(order.payment_status || 'legacy_unknown').replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2 flex-wrap">
                        <Button size="sm" variant="ghost" onClick={() => setSelectedOrder(order)} data-testid={`view-order-${order.id}`}><Eye className="h-4 w-4" /></Button>
                        {order.payment_status === 'unpaid' && order.status !== 'cancelled' && (
                          <Button size="sm" variant="outline" onClick={() => openPaymentDialog(order)} data-testid={`mark-paid-${order.id}`}>
                            <CreditCard className="me-1 h-4 w-4" /> Mark as Paid
                          </Button>
                        )}
                        {order.payment_status === 'paid' && canRefund && (
                          <Button size="sm" variant="outline" onClick={() => { setRefundOrder(order); setRefundReason(''); }} data-testid={`refund-${order.id}`}>
                            <RotateCcw className="me-1 h-4 w-4" /> Refund
                          </Button>
                        )}
                        <Select onValueChange={(val) => updateStatus(order.id, val)}>
                          <SelectTrigger className="w-28 h-8 text-xs" data-testid={`update-status-${order.id}`}><SelectValue placeholder="Update" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="preparing">Preparing</SelectItem>
                            <SelectItem value="ready">Ready</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="ghost" data-testid={`whatsapp-${order.id}`}><MessageSquare className="h-4 w-4" style={{ color: '#25D366' }} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-h-[86vh] w-[96vw] max-w-6xl overflow-hidden p-0">
          <DialogHeader className="border-b border-border/70 bg-muted/20 px-6 py-4">
            <DialogTitle className="text-xl font-semibold tracking-tight">Order Details</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="max-h-[74vh] overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
                <div className="space-y-5 lg:col-span-7">
                  <div className="rounded-lg border border-border/70 bg-card p-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Order</div>
                        <div className="mt-1 text-base font-semibold">#{selectedOrder.order_number}</div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Status</div>
                        <div className="mt-1"><Badge className={statusColors[selectedOrder.status]}>{selectedOrder.status}</Badge></div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Total</div>
                        <div className="mt-1 text-lg font-bold">{currency} {selectedOrder.total_amount.toFixed(2)}</div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Payment</div>
                        <div className="mt-1 flex items-center gap-2">
                          <Badge className={paymentStatusColors[selectedOrder.payment_status || 'legacy_unknown']}>
                            {(selectedOrder.payment_status || 'legacy_unknown').replace('_', ' ')}
                          </Badge>
                          {['paid', 'refunded'].includes(selectedOrder.payment_status) && (
                            <span className="text-sm capitalize">{selectedOrder.payment_method?.replaceAll('_', ' ')}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {selectedOrder.status === 'cancelled' && selectedOrder.payment_status === 'paid' && (
                      <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
                        This cancelled order is still paid and may require a refund.
                      </div>
                    )}
                    {selectedOrder.delivery_address && (
                      <div className="mt-4 border-t border-border/60 pt-3">
                        <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Delivery Address</div>
                        <div className="mt-1 text-sm leading-relaxed">{selectedOrder.delivery_address}</div>
                      </div>
                    )}
                    {selectedOrder.notes && (
                      <div className="mt-3 border-t border-border/60 pt-3">
                        <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Notes</div>
                        <div className="mt-1 text-sm leading-relaxed">{selectedOrder.notes}</div>
                      </div>
                    )}
                  </div>

                  <div className="rounded-lg border border-border/70 bg-card p-4">
                    <div className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">Items</div>
                    <div className="max-h-[300px] overflow-y-auto rounded-md border border-border/60">
                      <div className="divide-y divide-border/60">
                        {selectedOrder.items?.map((item, idx) => (
                          <div key={idx} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 px-3 py-2 text-sm">
                            <div className="font-medium">{item.name}</div>
                            <div className="text-muted-foreground">x{item.quantity}</div>
                            <div className="min-w-16 text-right font-semibold">{currency} {(item.price * item.quantity).toFixed(2)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-5 lg:col-span-5">
                  <OrderDocumentsPanel
                    selectedOrder={selectedOrder}
                    currency={currency}
                    activityLoading={activityLoading}
                    documentActivities={documentActivities}
                    openDocumentPreview={openDocumentPreview}
                    queueDocumentAction={queueDocumentAction}
                    hasDocumentActivity={hasDocumentActivity}
                    handleReprint={handleReprint}
                  />
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!paymentOrder} onOpenChange={(open) => { if (!open && !paymentSubmitting) setPaymentOrder(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Confirm Payment</DialogTitle></DialogHeader>
          {paymentOrder && (
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-3">
                <span className="font-medium">Grand Total</span>
                <span className="text-xl font-bold">{currency} {paymentOrder.total_amount.toFixed(2)}</span>
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod} disabled={paymentSubmitting}>
                  <SelectTrigger data-testid="confirm-payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="online_transfer_qr">Online Transfer / QR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {paymentMethod === 'cash' && (
                <>
                  <div>
                    <Label>Cash Received</Label>
                    <Input type="number" min="0" step="0.01" value={cashReceived} onChange={(event) => setCashReceived(event.target.value)} disabled={paymentSubmitting} data-testid="cash-received" />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Change</span>
                    <span className="font-bold">{currency} {Number.isFinite(cashChange) ? cashChange.toFixed(2) : '0.00'}</span>
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentOrder(null)} disabled={paymentSubmitting}>Cancel</Button>
            <Button onClick={confirmPayment} disabled={paymentSubmitting} data-testid="confirm-payment-btn">
              {paymentSubmitting ? 'Confirming...' : 'Confirm Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!refundOrder} onOpenChange={(open) => { if (!open && !refundSubmitting) setRefundOrder(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record Full Refund</DialogTitle></DialogHeader>
          {refundOrder && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Refund the full paid amount of {currency} {(refundOrder.paid_amount || refundOrder.total_amount).toFixed(2)} for {refundOrder.order_number}.
              </p>
              <div>
                <Label>Refund Reason</Label>
                <Textarea value={refundReason} onChange={(event) => setRefundReason(event.target.value)} disabled={refundSubmitting} data-testid="refund-reason" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOrder(null)} disabled={refundSubmitting}>Cancel</Button>
            <Button variant="destructive" onClick={confirmRefund} disabled={refundSubmitting || !refundReason.trim()} data-testid="confirm-refund-btn">
              {refundSubmitting ? 'Refunding...' : 'Confirm Full Refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OrderDocumentPreviewDialog
        previewOpen={previewOpen}
        setPreviewOpen={setPreviewOpen}
        previewLoading={previewLoading}
        previewDocument={previewDocument}
        previewDocumentType={previewDocumentType}
        refreshDocumentPreview={refreshDocumentPreview}
        saveAsPdf={saveAsPdf}
        browserPrint={browserPrint}
        pdfSaving={pdfSaving}
        printLoading={printLoading}
        thermalPreviewRef={thermalPreviewRef}
        closePreview={closePreview}
      />
    </div>
  );
};

export default Orders;
